/* eslint-disable react-hooks/exhaustive-deps */
import {Grid, TableFooter, TablePagination, TableRow} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {DialogType} from 'commons/Dialog/CustomDialog';
import {localize} from 'commons/LocalizedText/LocalizedText';
import {INITIAL_PAGE} from 'commons/Table/CustomTable';
import {CustomTableWithExternalPagination} from 'commons/Table/CustomTableWithExternalPagination';
import {
  companyRow,
  firstnameRow,
  lastnameRow,
  mailOrLoginRow,
  profileRow,
  programRow,
  statusRow
} from 'components/Administration/ManageContactRows';
import ExcelExportButton from 'components/ExcelExportButton/ExcelExportButton';
import {useDialog} from 'hook/DialogProvider';
import useQueryState from 'hook/UseQueryState';
import React, {useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {UserService} from 'services/UserService';
import {ParamTypes} from 'utils/constants/param.type';
import {MOBILE_BREAKPOINT} from 'utils/constants/responsive';
import {GENERATE_ROUTE, ROUTES} from 'utils/constants/routes';
import {convertJsonToXlsx} from 'utils/excelConverter';
import {CustomButton} from "commons/Button/CustomButton";
import {LaboUserSearch} from "commons/Filter/SearchFields/LaboUserSearch";

const useStyles = makeStyles((theme) => ({
  divider: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      marginBottom: theme.spacing(1)
    }
  },
  inputFields: {
    maxWidth: 600,
    width: '-webkit-fill-available',
    marginLeft: '20px'
  }
}));

const headers = [
  mailOrLoginRow,
  lastnameRow,
  firstnameRow,
  companyRow,
  statusRow,
  profileRow,
  programRow
];

export const ManageContact = () => {
  const classes = useStyles();
  const history = useHistory();
  const previousController = useRef();
  const showDialog = useDialog();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [total, setTotal] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useQueryState('rowsPerPage', 10, ParamTypes.NUMBER);
  const [page, setPage] = useQueryState('page', INITIAL_PAGE, ParamTypes.NUMBER);
  const { laboratory: laboratoryId } = useParams();
  const [contactGridFilterView, setContactGridFilterView] = useState({});

  const updateFilter = (filterData) => {
    setContactGridFilterView(filterData);
  }

  const updateData = () => {
    if (laboratoryId == null) {
      return;
    }
    setIsLoading(true);
    if (previousController.current) {
      previousController.current.abort();
    }
    const controller = new AbortController();
    const { signal } = controller;
    previousController.current = controller;

    UserService.searchUsersWithFiltersForAdminEcc({...contactGridFilterView, laboratoryId }, signal)
      .then((response) => {
        if (response) {
          setPage(0);
          setRows(response.users);
          setTotal(response.count);
        }
        setIsLoading(false);
      })
      .catch((errorMessage) => {
        if (errorMessage !== 'AbortError') {
          showDialog({
            variant: DialogType.ERROR,
            title: 'error',
            description: errorMessage
          });
        }
      });
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const excelExport = () => {
    setIsLoadingExport(true);
    if (laboratoryId == null) {
      return;
    }

    UserService.searchUsersWithFiltersForAdminEcc({...contactGridFilterView, laboratoryId })
      .then((response) => {
        if (response) {
          const date = new Date();
          const time = date.getTime();
          convertJsonToXlsx(response.users, `${localize('contactsExport')}${date.toLocaleDateString()}-${time}`);
        }
        setIsLoadingExport(false);
      })
      .catch((errorMessage) => {
        if (errorMessage !== 'AbortError') {
          showDialog({
            variant: DialogType.ERROR,
            title: 'error',
            description: errorMessage
          });
        }
        setIsLoadingExport(false);
      });
  }

  const openEditUser = row => {
    const editUserLink = GENERATE_ROUTE(
      ROUTES.ADMINISTRATION.USER_EDIT,
      { laboratory: laboratoryId, personContactDetailsId: row.personContactDetailsRecid }
    );
    history.push(editUserLink);
  }

  const openCreateUser = () => {
    const editUserLink = GENERATE_ROUTE(
      ROUTES.ADMINISTRATION.USER_CREATE,
      {laboratory: laboratoryId}
    );
    history.push(editUserLink);
  }

  useEffect(() => {
    updateData();
  }, []);

  useEffect(() => {
    setRows([]);
    updateData();
  }, [contactGridFilterView]);

  return (
    <div className={classes.marginTop}>
      <Grid
        alignItems="flex-start"
        container
        direction="column"
        justifyContent="space-evenly"
        spacing={15}
        wrap='nowrap'
      >
        <Grid
          item>
          <LaboUserSearch
            updateFilter={updateFilter}
          />
        </Grid>
      </Grid>
      <Grid
        alignItems="flex-end"
        container
        direction="column"
        justifyContent="space-evenly"
        spacing={15}
        wrap='nowrap'
      >
        <Grid
          item
        >
          <CustomButton
            onClick={() => openCreateUser()}>
            {localize('addUserButton')}
          </CustomButton>
        </Grid>
      </Grid>
      <CustomTableWithExternalPagination
        headers={headers}
        isLoading={isLoading}
        onRowCLick={(row) => openEditUser(row)}
        page={page}
        rowKey="mailOrLoginRow"
        rows={rows}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        total={total}
      />
      <Grid alignItems='center' className={classes.tableFooter} container justifyContent='space-between'>
        <Grid item>
          <ExcelExportButton
            handleExport={() => excelExport()}
            isLoadingExport={isLoadingExport}>
          </ExcelExportButton>
        </Grid>
        <Grid item>
          <TableFooter>
            <TableRow>
              <TablePagination
                backIconButtonText={localize('previousPage')}
                className={classes.overflowHidden}
                count={total}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to === -1 ? count : to} ${localize('to')} ${count}`}
                labelRowsPerPage={localize('rowsPerPage')}
                nextIconButtonText={localize('nextPage')}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </TableRow>
          </TableFooter>
        </Grid>
      </Grid>
    </div>
  );
};